import React, { createContext, useContext, useEffect, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { gql, GraphQLClient } from "graphql-request";
import { getEnviroment } from "../env";
import { getUserInfo } from "../pages/Login/AuthenticationService";

const IrisPermissions = {
  ASSET_MANAGE: "asset.manage",
} as const;

const GET_USER_PERMISSIONS = gql`
  query GetUserPermissions($accountId: ID!) {
    userCurrent {
      user {
        permissions(securableIds: [$accountId]) {
          securableId
          permissions
        }
      }
    }
  }
`;

interface SecurablePermission {
  securableId: string;
  permissions: string[];
}

interface UserCurrentResponse {
  userCurrent: {
    user: {
      permissions: SecurablePermission[] | null;
    } | null;
  } | null;
}

interface IrisPermissionsContextValue {
  isFleetAdmin: boolean;
}

const IrisPermissionsContext = createContext<IrisPermissionsContextValue>({
  isFleetAdmin: false
});

interface IrisPermissionsProviderProps {
  children: React.ReactNode;
}

export const IrisPermissionsProvider: React.FC<IrisPermissionsProviderProps> = ({ children }) => {
  const { authState } = useOktaAuth();
  const [isFleetAdmin, setIsFleetAdmin] = useState<boolean>(false);

  useEffect(() => {
    async function fetchPermissions() {
      if (!authState?.isAuthenticated || !authState?.idToken) {
        return;
      }

      const userInfo = getUserInfo(authState.accessToken);
      if (!userInfo?.accountId) {
        return;
      }

      try {
        const client = new GraphQLClient(
          `${getEnviroment().endpoints.graphQlManager}/GetUserPermissions`,
          { headers: { Authorization: `Bearer ${authState.idToken.idToken}` } }
        );
        const response = await client.request<UserCurrentResponse>(
          GET_USER_PERMISSIONS,
          { accountId: userInfo.accountId }
        );

        const user = response.userCurrent?.user;
        const accountPermissions = user?.permissions?.find(
          p => p.securableId === userInfo.accountId
        );
        const permissions = accountPermissions?.permissions ?? [];
        setIsFleetAdmin(permissions.includes(IrisPermissions.ASSET_MANAGE));
      } catch (err) {
        // Silently fail - user is not Fleet Admin if we can't fetch permissions
        setIsFleetAdmin(false);
      }
    }

    fetchPermissions();
  }, [authState?.isAuthenticated, authState?.idToken, authState?.accessToken]);

  return (
    <IrisPermissionsContext.Provider value={{ isFleetAdmin }}>
      {children}
    </IrisPermissionsContext.Provider>
  );
};

/**
 * Hook that returns Iris permissions for the current user.
 * Must be used within IrisPermissionsProvider.
 * 
 * @returns {{ isFleetAdmin: boolean }} - isFleetAdmin is true if user has asset.manage permission
 */
export function useIrisPermissions(): IrisPermissionsContextValue {
  return useContext(IrisPermissionsContext);
}
